import React from 'react'
import { Link } from 'react-router-dom'
import DOMPurify from 'dompurify'
import { useTranslation } from 'react-i18next'
import type { ConversationDisplayData } from './types'

/**
 * The embed's own conversation card.
 *
 * `components/conversations/Card.tsx` is imported by the main app in nine other
 * places (the conversations listing, MyMembership x4, partner Tracks). The embed
 * used to render it too, and inherited a header block with a hard
 * `height: 116px` and no overflow handling: a title long enough to wrap past two
 * lines drew straight over the host avatar and the card body, white-on-light-grey
 * and unreadable. Four of OOH's seven upcoming events do exactly that, because
 * their naming convention is "We Hold These Truths with <partner org>".
 *
 * This is a separate component rather than a fix to that one, so the embed can
 * keep diverging -- it renders at iframe widths, with no app chrome, to a public
 * audience -- without every change being a regression risk across ten call sites.
 * Nothing here is shared with the app: no Card.scss, no flexboxgrid, no MUI
 * theme, no Font Awesome. The layout is intrinsic instead of fixed, so the title
 * cannot overflow anything no matter how long it is.
 */

const DEFAULT_THEME_COLOR = '#f7bf14' // themes/palette.ts yellow500, TopicTheme's fallback
const DARK_INK = '#14213d'
const LIGHT_INK = '#ffffff'

function relativeLuminance(hex: string): number | null {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
  if (!match) return null

  const digits = match[1].length === 3 ? match[1].replace(/./g, (c) => c + c) : match[1]
  const channels = [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16) / 255)
  const linear = channels.map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)))

  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2]
}

function contrastRatio(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
}

/**
 * Pick header text that can actually be read on the topic's colour.
 *
 * TopicTheme hardcodes white, which is fine for OOH (#224088) and wrong for the
 * default (#f7bf14 -- white on that is a 1.7:1 ratio). The app can get away with
 * it because a signed-in user has context for a header they can't quite read; an
 * embed on a stranger's website is the only thing that audience ever sees.
 */
function inkFor(background: string): string {
  const luminance = relativeLuminance(background)
  if (luminance === null) return LIGHT_INK

  const onDark = contrastRatio(luminance, relativeLuminance(DARK_INK) as number)
  const onLight = contrastRatio(luminance, relativeLuminance(LIGHT_INK) as number)

  return onLight >= onDark ? LIGHT_INK : DARK_INK
}

const PinIcon: React.FC = () => (
  <svg className="embed-card__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z"
    />
  </svg>
)

const VideoIcon: React.FC = () => (
  <svg className="embed-card__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 3.5V7l-4 3.5Z" />
  </svg>
)

interface EmbedCardProps {
  conversation: ConversationDisplayData
}

const EmbedCard: React.FC<EmbedCardProps> = ({ conversation }) => {
  const { t } = useTranslation()

  const {
    id,
    slug,
    title,
    host,
    city,
    stateLevel,
    full,
    guestPolicy,
    registrationClosed,
    isVirtual,
    themeColor,
    dateLocally,
    startsAtLocally,
    dayLocally,
    timeZoneAbbr,
  } = conversation

  const background = themeColor || DEFAULT_THEME_COLOR
  const ink = inkFor(background)

  const isPrivate = guestPolicy === 'private'
  const badge = full || (registrationClosed && !isPrivate)
    ? t('conversation.isFull')
    : isPrivate
      ? t('conversation.isPrivate')
      : null

  const avatarUrl = host?.hasProfileImage ? host.profileImage?.primary?.url || host.profileImage?.url : null
  const initials = `${host?.firstName?.trim().charAt(0) || ''}${host?.lastName?.trim().charAt(0) || ''}`.toUpperCase()

  const where = isVirtual ? t('conversation.online') : [city, stateLevel].filter(Boolean).join(', ')

  return (
    <div className="embed-card-cell">
      {/*
        A react-router <Link>, and it has to stay one. EmbedLayout intercepts
        clicks in the capture phase and looks for the closest `a[href]` so the
        conversation opens in the parent window rather than loading the whole SPA
        inside the partner's 600px iframe. A <div onClick> here would render no
        anchor, that interceptor would find nothing, and the OOH-12 fix would go
        silently dead -- covered by embeds.spec.ts "clicking a card opens the
        parent window".
      */}
      <Link className="embed-card" to={`/${slug}/conversations/${id}`} aria-label={title}>
        <div className="embed-card__head" style={{ backgroundColor: background, color: ink }}>
          <div className="embed-card__when">
            <span className="embed-card__date">
              <span className="embed-card__day">{dayLocally}</span> {dateLocally}
            </span>
            <span className="embed-card__time">
              {startsAtLocally} <span className="embed-card__tz">{timeZoneAbbr}</span>
            </span>
          </div>

          {badge && <span className="embed-card__badge">{badge}</span>}

          {/*
            The whole defect, and the whole fix. The head is sized by its content
            instead of being pinned to 116px, so the title block grows to fit
            rather than drawing over the avatar and the card body below it.
            Nothing is shortened to make it fit: no ellipsis, no shrinking font.
            The `title` attribute is a hover affordance, not a fallback for text
            the card declined to show.
          */}
          <h3 className="embed-card__title" title={title}>
            <span>{title}</span>
          </h3>
        </div>

        <div className="embed-card__avatar" aria-hidden="true">
          {avatarUrl ? <img src={avatarUrl} alt="" /> : <span className="embed-card__initials">{initials}</span>}
        </div>

        <div className="embed-card__body">
          <p
            className="embed-card__host"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(t('conversation.isHosting', { name: host?.firstName || '' })),
            }}
          />
          {where && (
            <p className="embed-card__where">
              {isVirtual ? <VideoIcon /> : <PinIcon />}
              <span>{where}</span>
            </p>
          )}
        </div>
      </Link>
    </div>
  )
}

export default EmbedCard
